/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import '@/account/components/AddressValidation/AddressValidation.css';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { Header, ToggleButton } from '@adobe-commerce/elsie/components';
import { CustomerAddressesModel } from '@/account/data/models';

export interface AddressValidationProps extends HTMLAttributes<HTMLDivElement> {
  busy?: boolean;
  originalAddress: CustomerAddressesModel | null;
  selection: 'suggested' | 'original' | null;
  suggestedAddress: CustomerAddressesModel | null;
  onSelectionChange: (selection: 'suggested' | 'original') => void;
}

export function formatAddressLine(
  address: CustomerAddressesModel | null | undefined
): string[] {
  if (!address) return [];
  const name = `${address.firstName} ${address.lastName}`.trim();
  const street = address.street ?? '';
  const city = address.city ?? '';
  const region = address.region || undefined;
  const post = address.postcode ?? '';
  const country = address.countryCode ?? '';

  let cityRegion = '';
  if (city && region) {
    cityRegion = `${city}, ${region?.regionCode ?? ''}`.trim();
  } else if (city) {
    cityRegion = city;
  } else if (region) {
    cityRegion = region?.regionCode ?? '';
  }

  let cityRegionPost = cityRegion;
  if (post) {
    cityRegionPost = cityRegion ? `${cityRegion} ${post}` : post;
  }

  let line3 = cityRegionPost;
  if (country) {
    line3 = cityRegionPost ? `${cityRegionPost}, ${country}` : country;
  }

  return [name, street, line3];
}

const AddressBlock: FunctionComponent<{
  title: string;
  address: CustomerAddressesModel | null | undefined;
  selected: boolean;
  busy?: boolean;
  onChange: () => void;
}> = ({ title, address, selected, busy, onChange }) => {
  const [line1, line2, line3] = formatAddressLine(address);
  const label = (
    <div>
      <div className="account-address-validation__option-title">
        <strong>{title}</strong>
      </div>
      {line1 && <div>{line1}</div>}
      {line2 && <div>{line2}</div>}
      {line3 && <div>{line3}</div>}
    </div>
  );
  return (
    <div className="account-address-validation__option">
      <ToggleButton
        busy={busy}
        className="account-address-validation__radio"
        label={label}
        name="address-validation-choice"
        selected={selected}
        value={title}
        onChange={onChange}
      />
    </div>
  );
};

export const AddressValidation: FunctionComponent<AddressValidationProps> = ({
  busy = false,
  className,
  originalAddress,
  onSelectionChange,
  selection,
  suggestedAddress,
}) => {
  const hasSuggestion = !!suggestedAddress;
  const translations = useText({
    title: 'Account.AddressValidation.title',
    subtitle: 'Account.AddressValidation.subtitle',
    suggestedAddress: 'Account.AddressValidation.suggestedAddress',
    originalAddress: 'Account.AddressValidation.originalAddress',
  });

  return (
    <div className={classes(['account-address-validation', className])}>
      <Header
        className="account-address-validation__title"
        divider={false}
        title={translations.title}
      />
      <div className="account-address-validation__subtitle">
        {translations.subtitle}
      </div>

      <div
        className={classes([
          'account-address-validation__options',
          busy && 'account-address-validation__options--busy',
        ])}
      >
        {hasSuggestion && (
          <AddressBlock
            address={suggestedAddress}
            busy={busy}
            selected={selection === 'suggested'}
            title={translations.suggestedAddress}
            onChange={() => onSelectionChange('suggested')}
          />
        )}

        <AddressBlock
          address={originalAddress}
          busy={busy}
          selected={selection === 'original'}
          title={translations.originalAddress}
          onChange={() => onSelectionChange('original')}
        />
      </div>
    </div>
  );
};
